import { CloseOutlined, KeyOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { TeamIcon, UserIcon } from 'assets/images/svg';
import { entityType } from 'config/entityType';
import { groupPermissionsByEntityForGroup } from 'library/helpers/accessControl';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectPermissionsByGroupID } from 'reduxStore/permissions/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { Button, Col, Divider, Empty, Flex, Form, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const GrantPermissionsToGroupModal = ({ visible: visibleProp, setVisible: setVisibleProp, children, groupID }) => {
	const {
		token: {
			fontWeightStrong,
			fontSizeSM,
			paddingXL,
			paddingXS,
			paddingSM,
			paddingLG,
			colorIcon,
			paddingXXS,
			colorTextDescription
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const canManage = useManagePermittedRoles('aCPermissionManagement');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const teams = useSelector(selectTeams);
	const people = useSelector(selectPeople);
	const allPermissions = useSelector((state) => selectPermissionsByGroupID(state, groupID));
	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedPeople, setSelectedPeople] = useState([]);
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [removedPermissions, setRemovedPermissions] = useState([]);

	const [hasChanges, setHasChanges] = useState(false);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
		setSelectedPeople([]);
		setSelectedTeams([]);
		setRemovedPermissions([]);

		setHasChanges(false);
	};

	const handleSubmit = () => {
		// Add new permissions
		if (selectedPeople.length > 0) {
			dispatch(
				permissionsActions.grantPermission({
					assetGroupIDs: [groupID],
					personIDs: selectedPeople,
					teamIDs: [], // Explicitly direct to group
					permissionTypeID: 1
				})
			);
		}

		if (selectedTeams.length > 0) {
			dispatch(
				permissionsActions.grantPermission({
					assetGroupIDs: [groupID],
					teamIDs: selectedTeams,
					personIDs: [], // Standardized team-to-group flow
					permissionTypeID: 1
				})
			);
		}

		// Remove permissions
		if (removedPermissions.length > 0) {
			removedPermissions.forEach((perm) => {
				const isPerson = perm.entityTypeID === entityType.PERSON;
				const isTeamSource = perm.source === 'Team';

				dispatch(
					permissionsActions.revokePermission({
						assetGroupIDs: [groupID],
						permissionTypeID: 1,
						// If source is Team, we send the teamID; otherwise, explicit null.
						teamID: isTeamSource ? perm.teamID : null,
						personIDs: isPerson ? [perm.entityID] : [],
						teamIDs: !isPerson ? [perm.entityID] : []
					})
				);
			});
		}

		handleCancel();
	};

	const displayPermissions = groupPermissionsByEntityForGroup({
		permissions: allPermissions,
		people,
		teams
	});

	// Filter out permissions that are marked for removal
	const filteredPermissions = displayPermissions.filter(
		(perm) => !removedPermissions.some((removed) => removed.permissionID === perm.permissionID)
	);

	const grantedPersonIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.PERSON).map((p) => p.entityID)
	);

	const grantedTeamIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.TEAM).map((p) => p.entityID)
	);

	const peopleOptions = people
		.filter((p) => !grantedPersonIDs.has(p.personID) && !selectedPeople.includes(p.personID))
		.map((p) => ({ label: p.displayName, value: p.personID }));

	const teamOptions = teams
		.filter((t) => !grantedTeamIDs.has(t.teamID) && !selectedTeams.includes(t.teamID))
		.map((t) => ({ label: t.name, value: t.teamID }));

	const handleRemovePermission = (perm) => {
		setRemovedPermissions([...removedPermissions, perm]);
		setHasChanges(true);
	};

	const handleAddPerson = (personID) => {
		setSelectedPeople([...selectedPeople, personID]);
		setHasChanges(true);
	};

	const handleAddTeam = (teamID) => {
		setSelectedTeams([...selectedTeams, teamID]);
		setHasChanges(true);
	};

	const handleRemoveSelectedPerson = (personID) => {
		const updated = selectedPeople.filter((id) => id !== personID);
		setSelectedPeople(updated);
		setHasChanges(updated.length > 0 || selectedTeams.length > 0 || removedPermissions.length > 0);
	};

	const handleRemoveSelectedTeam = (teamID) => {
		const updated = selectedTeams.filter((id) => id !== teamID);
		setSelectedTeams(updated);
		setHasChanges(selectedPeople.length > 0 || updated.length > 0 || removedPermissions.length > 0);
	};

	const isLoading = loadingAddPermission || loadingDeletePermission;

	return (
		<>
			<Tooltip title={!canManage ? permissionDeniedMessage : 'Manage Permissions'} placement='top'>
				<span onClick={(e) => canManage && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<KeyOutlined color='currentColor' />}
							disabled={!canManage}
						>
							Manage Permissions
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={isLoading}>
								Cancel
							</Button>
							<Button onClick={handleSubmit} type='primary' loading={isLoading} disabled={!hasChanges}>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Permissions
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Manage who has access to this device group by adding or removing people and teams.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add People</Text>
							<Select
								showSearch
								placeholder='Select People'
								style={{ width: '100%' }}
								options={peopleOptions}
								value={null}
								onSelect={handleAddPerson}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add Teams</Text>
							<Select
								showSearch
								placeholder='Select Teams'
								style={{ width: '100%' }}
								options={teamOptions}
								value={null}
								onSelect={handleAddTeam}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Current Access Permissions</Text>
							{filteredPermissions.length === 0 &&
							selectedPeople.length === 0 &&
							selectedTeams.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No permissions assigned yet. Add people or teams using the dropdowns above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{/* Existing permissions with specialized metadata */}
									{filteredPermissions.map((item) => (
										<Tooltip key={`existing-${item.permissionID}`} title={item.tooltip}>
											<Tag
												color={item.badgeColor}
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													{item.Icon && <item.Icon color='currentColor' />}
													{item.label}
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemovePermission(item)}
													/>
												</Flex>
											</Tag>
										</Tooltip>
									))}
									{/* Newly selected people */}
									{selectedPeople.map((personID) => {
										const person = people.find((p) => p.personID === personID);
										if (!person?.displayName) return null;
										return (
											<Tag
												key={`new-person-${personID}`}
												color='purple'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<UserIcon color='currentColor' />
													{person.displayName} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveSelectedPerson(personID)}
													/>
												</Flex>
											</Tag>
										);
									})}

									{/* Newly selected teams */}
									{selectedTeams.map((teamID) => {
										const team = teams.find((t) => t.teamID === teamID);
										if (!team?.name) return null;
										return (
											<Tag
												key={`new-team-${teamID}`}
												color='cyan'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<TeamIcon color='currentColor' />
													{team.name} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveSelectedTeam(teamID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default GrantPermissionsToGroupModal;
